import { httpLogin, httpRefresh, httpCheckAuth, httpLogout } from '#api';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePages } from '../PagesProvider/PagesProvider';
import { ExpressApi } from '#api';

const AuthContext = React.createContext();

const useAuth = () => {
    return useContext(AuthContext);
};

const AuthProvider = ({ children }) => {
    const { adminPages } = usePages();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(JSON.parse(sessionStorage.getItem('user')) || null);
    const [accessToken, setAccessToken] = useState(null);
    const navigate = useNavigate();

    const applyAccessToken = useCallback((token) => {
        if (token) {
            ExpressApi.defaults.headers.common['Authorization'] = token;
        } else {
            delete ExpressApi.defaults.headers.common['Authorization'];
        }
        setAccessToken(token);
    }, []);

    useEffect(() => {
        setTimeout(() => setLoading(false), 1000);
    }, []);

    useEffect(() => {
        if (loading) return;
        sessionStorage.setItem('user', JSON.stringify(user));
    }, [loading, user]);

    const clear = useCallback(() => {
        setUser(null);
        applyAccessToken(null);
        sessionStorage.removeItem('user');
    }, [applyAccessToken]);

    useEffect(() => {
        if (!user) return;
        const interval = setInterval(
            async () => {
                const response = await httpRefresh();
                if (response.status === 'success') {
                    applyAccessToken(response.accessToken);
                } else {
                    clear();
                    navigate('/');
                }
            },
            10 * 60 * 1000,
        );
        return () => clearInterval(interval);
    }, [user, applyAccessToken, clear, navigate]);

    const isConnectionExist = useCallback(async () => {
        if (accessToken) {
            ExpressApi.defaults.headers.common['Authorization'] = accessToken;
            const responseAccess = await httpCheckAuth(accessToken);
            if (responseAccess.status === 'success' && responseAccess.auth) {
                return true;
            }
        }

        const responseRefresh = await httpRefresh();
        if (responseRefresh.status === 'success') {
            applyAccessToken(responseRefresh.accessToken);
            return true;
        }

        clear();
        return false;
    }, [accessToken, applyAccessToken, clear]);

    const login = async (loginEmail, loginPassword) => {
        setLoading(true);
        const response = await httpLogin(loginEmail, loginPassword);

        if (response.status !== 'success') {
            setLoading(false);
            return false;
        }

        const rUser = {
            firstName: response.firstName,
            lastName: response.lastName,
            permision: response.permision,
        };

        setUser(rUser);
        applyAccessToken(response.accessToken);
        navigate(`${adminPages.base}${adminPages.dashboard}`);

        setLoading(false);
        return true;
    };

    const logout = () => {
        setLoading(true);

        setTimeout(async () => {
            await httpLogout();
            clear();
            setLoading(false);
            navigate('/');
        }, 300);
    };

    const getUserData = useCallback(() => {
        return user || { firstName: '', lastName: '', permison: '' };
    }, [user]);

    const value = {
        login,
        logout,
        isConnectionExist,
        getUserData,
    };
    if (loading) return <></>;
    return (
        <AuthContext.Provider value={value}>
            <meta name="robots" content="noindex, nofollow" />
            {children}
        </AuthContext.Provider>
    );
};

export { useAuth };
export default AuthProvider;
